import { Box, Button, Popover, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface FormValues {
  title: string;
  url: string;
}

interface AddLinkPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSubmit: (title: string, url: string) => void;
  initialTitle?: string;
}

export const AddLinkPopover = ({
  open,
  anchorEl,
  onClose,
  onSubmit,
  initialTitle = '',
}: AddLinkPopoverProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: initialTitle,
      url: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        title: initialTitle,
        url: '',
      });
    }
  }, [open, initialTitle, reset]);

  const handleFormSubmit = useCallback(
    (data: FormValues) => {
      let url = data.url;

      // Check if URL has a protocol
      if (!/^[a-zA-Z]+:\/\//.exec(url)) {
        // No protocol, add https://
        url = `https://${url}`;
      } else if (url.startsWith('http://')) {
        // Replace http:// with https://
        url = `https://${url.slice(7)}`;
      }

      onSubmit(data.title, url);
      onClose();
    },
    [onSubmit, onClose],
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Link
        </Typography>
        <form>
          <Stack spacing={2}>
            <TextField
              {...register('title', { required: 'Title is required' })}
              label="Title"
              error={!!errors.title}
              helperText={errors.title?.message}
              fullWidth
              autoFocus
              size="small"
            />
            <TextField
              {...register('url', {
                required: 'URL is required',
              })}
              label="URL"
              error={!!errors.url}
              helperText={errors.url?.message}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onClose}>Cancel</Button>
              <Button onClick={handleSubmit(handleFormSubmit)} variant="contained">
                OK
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </Popover>
  );
};
